import React from 'react';
import PropTypes from 'prop-types';
import { injectTestId } from '@cvent/nucleus-test-automation';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var getSrcSet = function getSrcSet(_ref) {
  var breakpoints = _ref.breakpoints,
      supportsWebp = _ref.supportsWebp,
      width = _ref.width,
      src = _ref.src;

  if (!width) {
    return;
  }

  var andWebp = supportsWebp ? '&f=webp' : '';
  var justWebp = supportsWebp ? '?f=webp' : ''; // Create srcset string based on passed in breakpoints

  var breakpointSrcSet = breakpoints && // Only apply breakpoints that are smaller than the image's natural width
  breakpoints.filter(function (breakpoint) {
    return breakpoint < width;
  }).map(function (breakpoint) {
    return "".concat(src, "?d=").concat(breakpoint).concat(andWebp, " ").concat(breakpoint, "w");
  }).join(",\n        "); // This prevents `(unknown)` from being passed to the srcset attribute if the image is smaller than any of the passed in breakpoints

  var srcSet = breakpointSrcSet ? breakpointSrcSet + ",\n        ".concat(src).concat(justWebp, " ").concat(width, "w") : "".concat(src).concat(justWebp, " ").concat(width, "w");
  return srcSet;
};

var ResponsiveImage = function ResponsiveImage(props) {
  var src = props.src,
      supportsWebp = props.supportsWebp,
      breakpoints = props.breakpoints,
      width = props.width,
      srcSet = props.srcSet,
      onLoad = props.onLoad,
      onError = props.onError,
      testID = props.testID,
      rest = _objectWithoutProperties(props, ["src", "supportsWebp", "breakpoints", "width", "srcSet", "onLoad", "onError", "testID"]);

  var onLoadCallback = function onLoadCallback(event) {
    event.persist();

    if (onLoad) {
      onLoad(event);
    }
  };

  var onErrorCallback = function onErrorCallback(event) {
    event.persist();

    if (onError) {
      onError(event);
    }
  };

  function getImage() {
    return /*#__PURE__*/React.createElement("img", _extends({}, rest, {
      key: src
    }, injectTestId(testID), onError ? {
      onError: onErrorCallback
    } : {}, onLoad ? {
      onLoad: onLoadCallback
    } : {}, {
      srcSet: srcSet || getSrcSet({
        breakpoints: breakpoints,
        supportsWebp: supportsWebp,
        width: width,
        src: src
      }),
      src: supportsWebp ? "".concat(src, "?f=webp") : src
    }));
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, getImage());
};
ResponsiveImage.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Image URL */
  src: PropTypes.string.isRequired,

  /** Does the rendering browser support webp image formats */
  supportsWebp: PropTypes.bool,

  /** srcset string for the responsive image, takes priority over the breakpoint generated
   * srcset string. See (https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/srcset)
   */
  srcSet: PropTypes.string,

  /** Array of numbers representing different viewport breakpoints. The array is used to generate a srcset
   * string
   */
  breakpoints: PropTypes.arrayOf(PropTypes.number),

  /**
   * The width of the source image in pixels. Needed to generate srcset string, if srcSet prop isn't passed in
   */
  width: PropTypes.number,

  /** Function that executes after image has loaded  */
  onLoad: PropTypes.func,

  /** Function that executes if an error is encountered while loading the image */
  onError: PropTypes.func,

  /** For e2e tests (maps to `data-cvent-id`) */
  testID: PropTypes.string
} : {};

function defaultEqualityCheck(a, b) {
  return a === b;
}

function areArgumentsShallowlyEqual(equalityCheck, prev, next) {
  if (prev === null || next === null || prev.length !== next.length) {
    return false;
  }

  // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.
  var length = prev.length;
  for (var i = 0; i < length; i++) {
    if (!equalityCheck(prev[i], next[i])) {
      return false;
    }
  }

  return true;
}

function defaultMemoize(func) {
  var equalityCheck = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultEqualityCheck;

  var lastArgs = null;
  var lastResult = null;
  // we reference arguments instead of spreading them for performance reasons
  return function () {
    if (!areArgumentsShallowlyEqual(equalityCheck, lastArgs, arguments)) {
      // apply arguments instead of spreading for performance.
      lastResult = func.apply(null, arguments);
    }

    lastArgs = arguments;
    return lastResult;
  };
}

function getDependencies(funcs) {
  var dependencies = Array.isArray(funcs[0]) ? funcs[0] : funcs;

  if (!dependencies.every(function (dep) {
    return typeof dep === 'function';
  })) {
    var dependencyTypes = dependencies.map(function (dep) {
      return typeof dep;
    }).join(', ');
    throw new Error('Selector creators expect all input-selectors to be functions, ' + ('instead received the following types: [' + dependencyTypes + ']'));
  }

  return dependencies;
}

function createSelectorCreator(memoize) {
  for (var _len = arguments.length, memoizeOptions = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    memoizeOptions[_key - 1] = arguments[_key];
  }

  return function () {
    for (var _len2 = arguments.length, funcs = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      funcs[_key2] = arguments[_key2];
    }

    var recomputations = 0;
    var resultFunc = funcs.pop();
    var dependencies = getDependencies(funcs);

    var memoizedResultFunc = memoize.apply(undefined, [function () {
      recomputations++;
      // apply arguments instead of spreading for performance.
      return resultFunc.apply(null, arguments);
    }].concat(memoizeOptions));

    // If a selector is called with the exact same arguments we don't need to traverse our dependencies again.
    var selector = memoize(function () {
      var params = [];
      var length = dependencies.length;

      for (var i = 0; i < length; i++) {
        // apply arguments instead of spreading and mutate a local list of params for performance.
        params.push(dependencies[i].apply(null, arguments));
      }

      // apply arguments instead of spreading for performance.
      return memoizedResultFunc.apply(null, params);
    });

    selector.resultFunc = resultFunc;
    selector.dependencies = dependencies;
    selector.recomputations = function () {
      return recomputations;
    };
    selector.resetRecomputations = function () {
      return recomputations = 0;
    };
    return selector;
  };
}

var createSelector = createSelectorCreator(defaultMemoize);

/**
 * The function is to detect whether a browser supports WebP image format
 * by decoding a small WebP image that uses a particular feature.
 * Based upon https://developers.google.com/speed/webp/faq#in_your_own_javascript.
 * @param feature Can be one of 'lossy', 'lossless', 'alpha' or 'animation'. Defaults to 'lossy'.
 */

var supportsWebp = defaultMemoize(function () {
  var feature = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'lossy';
  return new Promise(function (resolve) {
    var testImages = {
      lossy: 'UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA',
      lossless: 'UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==',
      alpha: 'UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAARBxAR/Q9ERP8DAABWUDggGAAAABQBAJ0BKgEAAQAAAP4AAA3AAP7mtQAAAA==',
      animation: 'UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA'
    };
    var img = new Image();

    img.onload = function () {
      resolve(img.width > 0 && img.height > 0);
    };

    img.onerror = function () {
      resolve(false);
    };

    img.src = 'data:image/webp;base64,' + testImages[feature];
  }).catch(function () {
    return false;
  });
});

export default ResponsiveImage;
export { ResponsiveImage, supportsWebp };
//# sourceMappingURL=index.js.map
